/**
 * 章节回退处置 API client
 *
 * Phase 3-3 / Plan §5.6 B3-min / Design §8.2 content 权限点。
 *
 * 包装 axios apiClient 调用 admin-service /chapter-rollback/* 端点；
 * 返回类型与 admin-service rowToDto 返回形状对齐。
 */
import { apiClient } from './client';

// ── DTO 类型定义 ─────────────────────────────────────────────────────────────

/** 章节列表项 DTO（不含正文，减少 payload） */
export interface ChapterDto {
  chapterId: string;
  workId: string;
  title: string;
  bodyVersion: number;
  lockStatus: string;
  sortOrder: number;
  generationTaskId: string | null;
  createdAt: number;
  updatedAt: number;
}

/** 章节详情 DTO（含正文 body） */
export interface ChapterDetailDto {
  chapterId: string;
  workId: string;
  title: string;
  /** 正文（可能很长，前端用 Typography.Paragraph ellipsis 展示） */
  body: string | null;
  bodyVersion: number;
  lockStatus: string;
  sortOrder: number;
  generationTaskId: string | null;
  createdAt: number;
  updatedAt: number;
}

/** 快照历史 DTO */
export interface SnapshotDto {
  snapshotId: string;
  chapterId: string;
  titleBefore: string | null;
  /** 正文快照（截断到 500 字符，含 '...' 后缀） */
  contentBefore: string;
  bodyVersionBefore: number | null;
  triggerSource: string | null;
  canRollback: boolean;
  createdAt: number;
}

/** 关联 ai_task 信息 */
export interface RecentAiTaskDto {
  taskId: string;
  /** 对应 prompt_template_id（prompt 版本标识） */
  promptVersionId: string | null;
  status: string;
}

/** GET /chapters/:id 详情响应 */
export interface ChapterFullDetailDto {
  chapter: ChapterDetailDto;
  snapshots: SnapshotDto[];
  recentAiTask: RecentAiTaskDto | null;
}

/** GET /chapters 列表响应 */
export interface ListChaptersResponse {
  items: ChapterDto[];
  total: number;
  limit: number;
  offset: number;
}

/** POST /chapters/:id/rollback 响应 */
export interface RollbackChapterResponse {
  chapterId: string;
  bodyVersion: number;
  lockStatus: string;
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

// ── API 函数 ─────────────────────────────────────────────────────────────────

/**
 * GET /chapter-rollback/chapters?workId=xxx&limit=20&offset=0
 * 按 workId 分页列出章节。
 */
export async function listChaptersByWorkId(
  workId: string,
  limit = 20,
  offset = 0,
): Promise<ListChaptersResponse> {
  const resp = await apiClient.get<ServiceResponse<ListChaptersResponse>>(
    '/chapter-rollback/chapters',
    { params: { workId, limit, offset } },
  );
  return resp.data.data;
}

/**
 * GET /chapter-rollback/chapters/:id
 * 单章节详情（含正文 + snapshots 历史 + 关联 ai_task）。
 */
export async function getChapterDetail(chapterId: string): Promise<ChapterFullDetailDto> {
  const resp = await apiClient.get<ServiceResponse<ChapterFullDetailDto>>(
    `/chapter-rollback/chapters/${encodeURIComponent(chapterId)}`,
  );
  return resp.data.data;
}

/**
 * POST /chapter-rollback/chapters/:id/rollback
 * 回退章节到上一版本。
 *
 * @param reason      回退原因（5..200 字符）
 * @param notifyAuthor 是否通知作者（默认 true；通知由 content-pipeline best-effort 发送）
 */
export async function rollbackChapter(
  chapterId: string,
  reason: string,
  notifyAuthor = true,
): Promise<RollbackChapterResponse> {
  const resp = await apiClient.post<ServiceResponse<RollbackChapterResponse>>(
    `/chapter-rollback/chapters/${encodeURIComponent(chapterId)}/rollback`,
    { reason, notifyAuthor },
  );
  return resp.data.data;
}
